import React, { useState } from 'react';
import Swal from 'sweetalert2';
import ChangePassword from './ChangePassword';
import CompanySelector from './CompanySelector';
import './Login.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const api = axios.create({ baseURL: 'https://django-backend-115705416374.asia-south1.run.app' });
// const api = axios.create({ baseURL:'http://localhost:8000' });

export default function VendorLogin({ onShowRegister, onShowForgot, setSelected }) {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [otp, setOtp] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [tempAccessToken, setTempAccessToken] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      otpSent ? handleVerifyOtp() : handleSendOtp();
    }
  };

  const resetFields = () => {
    setCredentials({ username: '', password: '' });
    setOtp('');
    setOtpSent(false);
  };

  // Step 1: Send OTP
  const handleSendOtp = async () => {
    const usernameTrimmed = (credentials?.username || '').trim();
    const passwordTrimmed = (credentials?.password || '').trim();

    if (!usernameTrimmed || !passwordTrimmed) {
      Swal.fire({ icon: 'warning', title: 'All Fields Required', text: 'Please fill username and password.' });
      return;
    }

    setLoading(true);
    try {
      // If backend returns 429, execution jumps to catch block immediately
      await api.post('/api/request-login-otp/', {
        username: usernameTrimmed,
        password: passwordTrimmed
      });

      // Success case (200 OK)
      setOtpSent(true);
      Swal.fire({ icon: 'success', title: 'OTP sent!', text: 'Check your email.' });

    } catch (err) {
      // Identify the 429 error from your backend logs
      const isRateLimited = err.response && err.response.status === 429;

      if (isRateLimited) {
        // ✅ THIS IS THE FIX:
        // Even though it "Failed" to send a NEW otp, we must show the INPUT 
        // field so the user can type the one they already have.
        setOtpSent(true);

        Swal.fire({
          icon: 'info', // Changed to 'info' because the user can still proceed
          title: 'OTP Already Active',
          text: err.response.data.error || 'Please check your email for the existing OTP.'
        });
      } else {
        // Actual failure (wrong password, etc.)
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: err.response?.data?.error || 'Invalid credentials'
        }).then(() => {
          setCredentials({ username: '', password: '' });
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async () => {
    const usernameTrimmed = (credentials?.username || '').trim();
    const passwordTrimmed = (credentials?.password || '').trim();
    const otpTrimmed = (otp || '').trim();

    if (!otpTrimmed) {
      Swal.fire({
        icon: 'warning',
        title: 'OTP Required',
        text: 'Please enter the OTP sent to your email.',
      });
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/verify-login-otp/', {
        username: usernameTrimmed,
        password: passwordTrimmed,
        otp: otpTrimmed,
      },
        {
          withCredentials: true // 👈 THIS IS THE CRITICAL ADDITION
        }
      );

      // Store tokens
      if (res.data.access) {
        localStorage.setItem('access', res.data.access);
        setAccessToken(res.data.access);
      }
      if (res.data.refresh) localStorage.setItem('refresh', res.data.refresh);

      // Store login type for routing
      if (res.data.login_type === 'reupload') {
        localStorage.setItem('vendor_login_type', 'reupload');
      } else {
        localStorage.setItem('vendor_login_type', 'final');
      }

      // Handle temp password
      if (res.data.temp_password_expired) {
        Swal.fire({
          icon: 'warning',
          title: res.data.message,
          showCancelButton: true,
          confirmButtonText: 'Change Password',
          cancelButtonText: 'Change Password Later',
        }).then((result) => {
          if (result.isConfirmed) {
            setTempAccessToken(res.data.access);
            setShowChangePassword(true);
          } else {
            resetFields();
          }
        });
      } else {
        // ✅ MULTI-COMPANY CHECK
        // Use the flag from backend response directly
        if (res.data.requires_company_selection) {
          // Multiple companies -> Show selector
          // Clear any error flags before showing selector
          localStorage.removeItem('company_selector_error');
          setShowCompanySelector(true);
        } else {
          // Single company -> Backend already auto-selected it
          // No need to show selector, directly redirect to dashboard
          // Clear any error flags before redirecting
          localStorage.removeItem('company_selector_error');
          resetFields();
          // Navigate to root - dashboard will load with auto-selected company and correct logo
          window.location.href = '/';
        }
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: err.response?.data?.error || err.message,
      }).then(() => {
        // Clear all fields on failure
        resetFields();
      });
    } finally {
      setLoading(false);
    }
  };

  if (showChangePassword) {
    return (
      <ChangePassword
        username={credentials.username}
        tempAccessToken={tempAccessToken}
        onPasswordChanged={() => {
          resetFields();
          setShowChangePassword(false);
        }}
      />
    );
  }

  // ✅ Show Company Selector if multiple companies
  if (showCompanySelector) {
    return (
      <CompanySelector
        token={accessToken}
        onCompanySelected={(company) => {
          // After selection, navigate to dashboard with correct company
          resetFields();
          // Clear error flag on successful selection
          localStorage.removeItem('company_selector_error');
          // Navigate to dashboard - it will load with correct company logo
          window.location.href = '/';
        }}
      />
    );
  }

  return (
    <div className="login-container" style={{ justifyContent: 'flex-start', paddingTop: '70px' }}>
      <div className="logos-container">
        <img draggable={false}
          src={process.env.PUBLIC_URL + "/Main.png"}
          alt="Main Logo"
          className="logo"
        />
        <img draggable={false}
          src={process.env.PUBLIC_URL + "/Invenia.png"}
          alt="Invenia Logo"
          className="logo logo-second"
        />
        <img draggable={false}
          src={process.env.PUBLIC_URL + "/STL_Logo.png"}
          alt="STL Logo"
          className="logo logo-third"
        />
      </div>

      <div className="auth-box login-page">
        <h2 className="page-title">Vendor Login</h2>

        <input
          type="text"
          placeholder="Username"
          value={credentials.username}
          onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
          onKeyDown={handleKeyPress}
          disabled={otpSent || loading}
          readOnly={loading}
        />

        <input
          type="password"
          placeholder="Password"
          value={credentials.password}
          onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
          onKeyDown={handleKeyPress}
          disabled={otpSent || loading}
          readOnly={loading}
        />

        {otpSent && (
          <input
            type="text"
            className="otp-input"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={loading}
            readOnly={loading}
          />
        )}

        <button onClick={otpSent ? handleVerifyOtp : handleSendOtp} disabled={loading}>
          {loading ? 'Processing...' : otpSent ? 'Verify OTP & Login' : 'Send OTP'}
        </button>

        <p className="forgot-link">
          <button className="link-btn" onClick={onShowForgot} disabled={loading}>
            Forgot Password?
          </button>
        </p>

        {/* ✅ Back Button */}
        {setSelected && (
          <button className="back-btn" onClick={() => !loading && setSelected(null)} disabled={loading}>
            Back
          </button>
        )}
      </div>
      <div style={{ position: 'fixed', bottom: 0, left: 0, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px 0', zIndex: 1000 }}>
        <span style={{ fontWeight: '600', color: '#fff', marginRight: '10px' }}>Powered by</span>
        <img draggable={false} src="/Main.png" alt="STL-DIGITAL" style={{ height: '30px' }} />
      </div>
    </div >
  );
}
